import { Time } from "./time";
import { TermsAndPrivacy } from "./terms_and_privacy";
import { DeviceType } from "@helpers/types";
import { TranslationService } from "@core-services/translation.service";
import {
	APPROPRIATENESS_APPROPRIATE,
	APPROPRIATENESS_NOT_APPROPRIATE,
	APPROPRIATENESS_NOT_EXPERIENCED,
	APPROPRIATENESS_UNKNOWN
} from "@helpers/constants";

export enum UserVerificationState {
	UNVERIFIED = "unverified",
	VERIFIED = "verified",
	STARTED = "started",
	DOCUMENTED = "documented",
	PENDING_APPROVAL = "pending_approval"
}

export class User extends TermsAndPrivacy {
	private email: string = "";
	private password: string = "";
	private language: string = "";
	private loggedIn: boolean = false;
	private TwoFA: boolean = false;
	private fiat_id: string = "";
	private currency: string = "";
	private last_last_login: Time = new Time();
	private password_reset: Time = new Time();
	private withdraw_allowed: Time = new Time();
	private enforce_two_factor_auth = false;
	private auth_code: string = "";
	private verified: string = UserVerificationState.UNVERIFIED;

	private device_id: string = "";
	private unique_payment_number: string = "";
	public referee_key: string = "";
	private is_affiliate: boolean = false;
	private expiration_sec: string = "";
	private two_fa_active_ever: boolean = false;

	private ignoredVerify: boolean = false;
	private ignored2FA: boolean = false;

	private device_type: DeviceType = "pc";

	private account_type: string = "";
	private account_status: string = "";
	private digital_securities_trading_appropriateness: string = "";
	private has_plus_support: boolean = false;
	private ld_secure_mode_hash: string = "";
	private citizenship_country_iso: string = "";
	private country_id: string = "";

	public constructor() {
		super();
		this.unset();
	}

	public logout(): void {
		this.unset();
	}

	public setLanguage(language: string): void {
		this.language = language;
	}

	public getLanguage(short: boolean = false): string {
		if (short) {
			return this.getShortLang();
		}
		return this.language;
	}

	public get2FA(): boolean {
		return this.TwoFA;
	}

	public setTwoFa(b: boolean): void {
		this.TwoFA = b;
	}

	public ignores2FA(): void {
		this.ignored2FA = true;
	}

	public getIgnore2FA(): boolean {
		return this.ignored2FA;
	}

	public getTwoFAEverActive(): boolean {
		return this.two_fa_active_ever;
	}

	public setTwoFAEverActive(b: boolean): void {
		this.two_fa_active_ever = b;
	}

	public setLoggin(b: boolean): void {
		this.loggedIn = b;
	}

	public unset(): void {
		this.email = "";
		this.password = "";
		this.language = TranslationService.startsWithNaviLanguage();
		this.loggedIn = false;
		this.TwoFA = false;
		this.two_fa_active_ever = false;
		this.device_id = "";
	}

	public get isLoggedIn(): boolean {
		return this.loggedIn;
	}

	public getUserCurrency(): string {
		return this.currency;
	}

	public getFiatId(): string {
		return this.fiat_id;
	}

	public getLoggedIn(): boolean {
		return this.isLoggedIn;
	}

	public setFiatId(fiatId: string): void {
		this.fiat_id = fiatId;
	}

	public getEmail(): string {
		return this.email;
	}

	public get2FAcode(): string {
		return this.auth_code;
	}

	public set2fa_code(s: string): void {
		this.auth_code = s;
	}

	public getDeviceId(): string {
		return this.device_id;
	}

	public setDeviceId(value: string): void {
		this.device_id = value;
	}

	public setDeviceType(deviceType: DeviceType): void {
		this.device_type = deviceType;
	}

	public getDeviceType(): DeviceType {
		return this.device_type;
	}

	public getPaymentReference(): string {
		return this.unique_payment_number;
	}

	public profileModifiable(): boolean {
		return !this.isVerificationPending() && !this.isVerified() && !this.isDocumented();
	}

	public isVerified(): boolean {
		return this.getVerified() === UserVerificationState.VERIFIED;
	}

	public isUnverified(): boolean {
		return (
			this.getVerified() === UserVerificationState.UNVERIFIED ||
			this.isVerificationStarted() ||
			this.isVerificationPending()
		);
	}

	public static isNotYetVerified(state: string): boolean {
		return (
			state === UserVerificationState.UNVERIFIED ||
			state === UserVerificationState.PENDING_APPROVAL ||
			state === UserVerificationState.STARTED
		);
	}

	public isVerificationStarted(): boolean {
		return this.getVerified() === UserVerificationState.STARTED;
	}

	public isDocumented(): boolean {
		return this.getVerified() === UserVerificationState.DOCUMENTED;
	}

	public isVerificationPending(): boolean {
		return this.getVerified() === UserVerificationState.PENDING_APPROVAL;
	}

	public getVerified(): string {
		return this.verified;
	}

	public setVerified(s: string): void {
		this.verified = s;
	}

	public hasIgnoredVerify(): boolean {
		return this.ignoredVerify;
	}

	public ignoreVerify(): void {
		this.ignoredVerify = true;
	}

	public get _email(): string {
		return this.email;
	}

	public set _email(s: string) {
		this.email = s;
	}

	public get _password(): string {
		return this.password;
	}

	public set _password(s: string) {
		this.password = s;
	}

	public get _auth_code(): string {
		return this.auth_code;
	}

	public set _auth_code(s: string) {
		this.auth_code = s;
	}

	public get _ld_secure_mode_hash(): string {
		return this.ld_secure_mode_hash;
	}

	public needs2FA(): boolean {
		return this.enforce_two_factor_auth;
	}

	public getWithdrawAllowed(): Time {
		return this.withdraw_allowed;
	}

	public isWithdrawTransactionAllowed(): boolean {
		return Date.now() > this.withdraw_allowed.getUnix();
	}

	public getLastLogin(): Time {
		return this.last_last_login;
	}

	public getExpirationSec(): string {
		return this.expiration_sec;
	}

	public resetData(): void {
		this.email = "";
		this.password = "";
		this.auth_code = "";
	}

	public getShortLang(): string {
		return TranslationService.getLanguageIsoCode(this.language);
	}

	public getRefereeKey(): string {
		return this.referee_key;
	}

	public withDrawLockHours(): number {
		return Math.ceil((this.withdraw_allowed.getUnix() - this.password_reset.getUnix()) / (1000 * 3600));
	}

	public isAffiliate(): boolean {
		return this.is_affiliate;
	}

	public getAccountType(): string {
		return this.account_type;
	}

	public getAccountStatus(): string {
		return this.account_status;
	}

	public isBusinessUser(): boolean {
		return this.account_type === "business_user" || this.account_type === "institutional_user";
	}

	public hasPlusSupport(): boolean {
		return this.has_plus_support;
	}

	public getTradingAppropriateness(): string {
		return this.digital_securities_trading_appropriateness;
	}

	public setTradingAppropriateness(s: string): void {
		this.digital_securities_trading_appropriateness = s;
	}

	public needsStockTest(): boolean {
		return this.getTradingAppropriateness() === APPROPRIATENESS_UNKNOWN;
	}

	public needsRiskToggle(): boolean {
		return this.isStockNotAppropriate() || this.isStockNotExperienced();
	}

	public isStockAppropriate(): boolean {
		return this.getTradingAppropriateness() === APPROPRIATENESS_APPROPRIATE;
	}

	public isStockNotAppropriate(): boolean {
		return this.getTradingAppropriateness() === APPROPRIATENESS_NOT_APPROPRIATE;
	}

	public isStockNotExperienced(): boolean {
		return this.getTradingAppropriateness() === APPROPRIATENESS_NOT_EXPERIENCED;
	}

	public isStockNotAppropriateOrNotExperienced(): boolean {
		return this.isStockNotAppropriate() || this.isStockNotExperienced();
	}
	public isUK(): boolean {
		return this.citizenship_country_iso === "GB";
	}

	public getCitizenshipCountryIso(): string {
		return this.citizenship_country_iso;
	}

	public getCountryId(): string {
		return this.country_id;
	}
}
